import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Filter, RefreshCw, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Building2, MapPin, Calendar, IndianRupee, ExternalLink,
  CheckCircle, Archive, AlertTriangle, Clock, Eye, Edit2,
  FileText, Loader2, X, Gavel, TrendingDown, Home, Copy,
  Plus, Timer, ArrowUpDown, Tag, Building, Ruler, StickyNote
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Status badge colors
const getStatusBadge = (status) => {
  const statusConfig = {
    'New': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
    'Active': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
    'Updated': { bg: 'bg-amber-100', text: 'text-amber-700', icon: RefreshCw },
    'Needs Review': { bg: 'bg-purple-100', text: 'text-purple-700', icon: AlertTriangle },
    'Duplicate': { bg: 'bg-slate-100', text: 'text-slate-600', icon: Copy },
    'Expired': { bg: 'bg-red-100', text: 'text-red-700', icon: X },
    'Archived': { bg: 'bg-slate-100', text: 'text-slate-500', icon: Archive },
  };
  
  const config = statusConfig[status] || { bg: 'bg-slate-100', text: 'text-slate-600', icon: Clock };
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.bg} ${config.text} gap-1`}>
      <Icon size={12} />
      {status}
    </Badge>
  );
};

// Priority badge
const getPriorityBadge = (priority) => {
  if (!priority) return null;
  const config = {
    'High': { bg: 'bg-red-100', text: 'text-red-700' },
    'Medium': { bg: 'bg-amber-100', text: 'text-amber-700' },
    'Low': { bg: 'bg-slate-100', text: 'text-slate-600' },
  };
  const c = config[priority] || config['Low'];
  return <Badge className={`${c.bg} ${c.text} text-xs`}>{priority}</Badge>;
};

// Days to auction badge with color coding
const getDaysToAuctionBadge = (days) => {
  if (days === null || days === undefined) return <span className="text-slate-400">—</span>;
  
  if (days < 0) {
    return <Badge className="bg-red-100 text-red-700">Passed</Badge>;
  }
  if (days <= 3) {
    return <Badge className="bg-red-100 text-red-700 animate-pulse">{days}d</Badge>;
  }
  if (days <= 7) {
    return <Badge className="bg-orange-100 text-orange-700">{days}d</Badge>;
  }
  return <Badge className="bg-slate-100 text-slate-600">{days}d</Badge>;
};

// Format currency in Lakhs
const formatLakhs = (value) => {
  if (!value && value !== 0) return '—';
  return `₹${value.toLocaleString('en-IN')} L`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Property types
const PROPERTY_TYPES = [
  'Residential Flat',
  'Residential House',
  'Residential Plot',
  'Commercial Shop',
  'Commercial Office',
  'Commercial Warehouse',
  'Industrial',
  'Agricultural Land',
  'Mixed Use',
  'Other'
];

// Occupancy statuses
const OCCUPANCY_STATUSES = ['Vacant', 'Occupied', 'Unknown'];

// Priority tags
const PRIORITY_TAGS = ['High', 'Medium', 'Low'];

const AuctionSupply = ({ user }) => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    city: '',
    property_type: '',
    source_name: '',
    bank: '',
    priority_tag: '',
    search: '',
    auction_date_from: '',
    auction_date_to: '',
    reserve_price_min: '',
    reserve_price_max: '',
    discount_min: '',
    discount_max: '',
    expiring_within: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Lookup data
  const [banks, setBanks] = useState([]);
  const [cities, setCities] = useState([]);
  
  // Detail drawer
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Add Property Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    property_type: 'Other',
    city: '',
    locality: '',
    full_address: '',
    area_sqft: '',
    reserve_price: '',
    emd: '',
    auction_date: '',
    bank_or_institution: '',
    occupancy_status: 'Unknown',
    source_name: 'Manual',
    source_url: '',
    estimated_market_price: '',
    internal_notes: '',
    priority_tag: '',
  });
  const [addingProperty, setAddingProperty] = useState(false);
  
  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);
  const [showSyncDetails, setShowSyncDetails] = useState(false);

  // Fetch lookup data
  const fetchLookups = useCallback(async () => {
    try {
      const [banksRes, citiesRes] = await Promise.all([
        api.getAuctionBanks(),
        api.getAuctionCities()
      ]);
      setBanks(banksRes.data.banks || []);
      setCities(citiesRes.data.cities || []);
    } catch (error) {
      console.error('Failed to load lookups:', error);
    }
  }, []);

  // Fetch auction supply list
  const fetchAuctions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
        )
      };
      
      const response = await api.getAuctionSupply(params);
      setRecords(response.data.records || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load auction supply');
      if (error.response?.status === 403) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, sortBy, sortOrder, navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getAuctionSupplyStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);
  
  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await api.getLatestSyncStatus();
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  }, []);

  useEffect(() => {
    fetchAuctions();
    fetchStats();
    fetchLookups();
    fetchSyncStatus();
  }, [fetchAuctions, fetchStats, fetchLookups, fetchSyncStatus]);

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Sync auction supply
  const handleSync = async (source = 'all') => {
    setSyncing(true);
    try {
      console.log('Starting sync with source:', source);
      const response = await api.syncAuctionSupply(source);
      console.log('Sync response:', response.data);
      const data = response.data;
      toast.success(
        <div>
          <p className="font-medium">{data.message}</p>
          <p className="text-xs mt-1">
            Fetched: {data.fetched} | Created: {data.created} | Updated: {data.updated}
          </p>
        </div>
      );
      fetchAuctions();
      fetchStats();
      fetchSyncStatus();
      fetchLookups();
    } catch (error) {
      console.error('Sync error:', error);
      console.error('Sync error response:', error.response);
      const errorMsg = error.response?.data?.detail || 
                       error.message || 
                       (error.code === 'ECONNABORTED' ? 'Request timed out - sync may still be running in background' : 'Sync failed');
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  // Add new property
  const handleAddProperty = async () => {
    if (!addForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setAddingProperty(true);
    try {
      const payload = {
        ...addForm,
        reserve_price: addForm.reserve_price ? parseFloat(addForm.reserve_price) : null,
        emd: addForm.emd ? parseFloat(addForm.emd) : null,
        estimated_market_price: addForm.estimated_market_price ? parseFloat(addForm.estimated_market_price) : null,
        area_sqft: addForm.area_sqft ? parseFloat(addForm.area_sqft) : null,
      };
      
      await api.createAuctionProperty(payload);
      toast.success('Property added successfully');
      setShowAddDialog(false);
      setAddForm({
        title: '',
        property_type: 'Other',
        city: '',
        locality: '',
        full_address: '',
        area_sqft: '',
        reserve_price: '',
        emd: '',
        auction_date: '',
        bank_or_institution: '',
        occupancy_status: 'Unknown',
        source_name: 'Manual',
        source_url: '',
        estimated_market_price: '',
        internal_notes: '',
        priority_tag: '',
      });
      fetchAuctions();
      fetchStats();
      fetchLookups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add property');
    } finally {
      setAddingProperty(false);
    }
  };

  // Open property detail
  const openDetail = async (property) => {
    setDetailLoading(true);
    setShowDetail(true);
    setSelectedProperty(property);
    setEditMode(false);
    
    try {
      const response = await api.getAuctionProperty(property.property_id);
      setSelectedProperty(response.data.property);
      setAuditLogs(response.data.audit_logs || []);
      setEditForm(response.data.property);
    } catch (error) {
      toast.error('Failed to load property details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Save edits
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateAuctionProperty(selectedProperty.property_id, editForm);
      toast.success('Property updated');
      setEditMode(false);
      fetchAuctions();
      fetchStats();
      // Refresh detail
      const response = await api.getAuctionProperty(selectedProperty.property_id);
      setSelectedProperty(response.data.property);
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Verify property
  const handleVerify = async () => {
    try {
      await api.verifyAuctionProperty(selectedProperty.property_id);
      toast.success('Property verified');
      fetchAuctions();
      fetchStats();
      const response = await api.getAuctionProperty(selectedProperty.property_id);
      setSelectedProperty(response.data.property);
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    }
  };

  // Archive property
  const handleArchive = async () => {
    if (!window.confirm('Are you sure you want to archive this property?')) return;
    try {
      await api.archiveAuctionProperty(selectedProperty.property_id);
      toast.success('Property archived');
      fetchAuctions();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    }
  };

  // Mark as duplicate
  const handleMarkDuplicate = async () => {
    const duplicateOf = window.prompt('Enter the Property ID of the original record:');
    if (!duplicateOf) return;
    try {
      await api.markAuctionDuplicate(selectedProperty.property_id, duplicateOf);
      toast.success('Marked as duplicate');
      fetchAuctions();
      fetchStats();
      const response = await api.getAuctionProperty(selectedProperty.property_id);
      setSelectedProperty(response.data.property);
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as duplicate');
    }
  };

  // Quick filter buttons
  const applyQuickFilter = (filterType) => {
    const newFilters = { ...filters, status: '', expiring_within: '' };
    
    switch (filterType) {
      case 'expiring_3d':
        newFilters.expiring_within = '3';
        break;
      case 'expiring_7d':
        newFilters.expiring_within = '7';
        break;
      case 'expiring_30d':
        newFilters.expiring_within = '30';
        break;
      case 'needs_review':
        newFilters.status = 'Needs Review';
        break;
      case 'high_priority':
        newFilters.priority_tag = 'High';
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      city: '',
      property_type: '',
      source_name: '',
      bank: '',
      priority_tag: '',
      search: '',
      auction_date_from: '',
      auction_date_to: '',
      reserve_price_min: '',
      reserve_price_max: '',
      discount_min: '',
      discount_max: '',
      expiring_within: '',
    });
  };

  // Sortable column header
  const SortableHeader = ({ field, children }) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === field ? (
          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        ) : (
          <ArrowUpDown size={12} className="text-slate-400" />
        )}
      </div>
    </th>
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="home-btn">
                <Home size={20} className="text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Gavel size={22} className="text-amber-600" />
                  Auction Supply
                </h1>
                <p className="text-sm text-slate-500">Manage auction properties from public sources</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="bg-amber-600 hover:bg-amber-700 gap-2"
                data-testid="add-property-btn"
              >
                <Plus size={14} />
                Add Property
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
                className="gap-2"
                data-testid="sync-btn"
              >
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Sync
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-slate-100' : ''}
                data-testid="filter-toggle-btn"
              >
                <Filter size={14} />
              </Button>
            </div>
          </div>

          {/* Stats Strip */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
              <div className="p-2 bg-blue-50 rounded-lg text-center border border-blue-100">
                <p className="text-lg font-bold text-blue-700">{stats.by_status?.New || 0}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg text-center border border-emerald-100">
                <p className="text-lg font-bold text-emerald-700">{stats.active_count || 0}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg text-center border border-purple-100">
                <p className="text-lg font-bold text-purple-700">{stats.by_status?.['Needs Review'] || 0}</p>
                <p className="text-xs text-slate-500">Needs Review</p>
              </div>
              <div 
                className="p-2 bg-red-50 rounded-lg text-center border border-red-100 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => applyQuickFilter('expiring_3d')}
              >
                <p className="text-lg font-bold text-red-700">{stats.expiring_3d || 0}</p>
                <p className="text-xs text-slate-500">Expiring 3d</p>
              </div>
              <div 
                className="p-2 bg-orange-50 rounded-lg text-center border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors"
                onClick={() => applyQuickFilter('expiring_7d')}
              >
                <p className="text-lg font-bold text-orange-700">{stats.expiring_7d || 0}</p>
                <p className="text-xs text-slate-500">Expiring 7d</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-lg text-center border border-slate-200">
                <p className="text-lg font-bold text-slate-700">{stats.by_status?.Expired || 0}</p>
                <p className="text-xs text-slate-500">Expired</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                <p className="text-lg font-bold text-emerald-700">{formatLakhs(stats.total_reserve_value_lacs)}</p>
                <p className="text-xs text-slate-500">Total Value</p>
              </div>
            </div>
          )}

          {/* Sync Status Card */}
          {syncStatus && (
            <div 
              className="mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => setShowSyncDetails(!showSyncDetails)}
              data-testid="sync-status-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} className="text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Last Sync:</span>
                    {syncStatus.latest_sync ? (
                      <>
                        <span className="text-sm text-slate-600">
                          {formatDate(syncStatus.latest_sync.sync_start_time)}
                        </span>
                        <Badge className={`${syncStatus.latest_sync.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : syncStatus.latest_sync.status === 'running' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                          {syncStatus.latest_sync.status}
                        </Badge>
                      </>
                    ) : (
                      <span className="text-sm text-slate-400">Never synced</span>
                    )}
                  </div>
                  
                  {/* Today's Summary */}
                  {syncStatus.summary && (
                    <div className="hidden sm:flex items-center gap-3 text-xs">
                      <span className="text-slate-400">|</span>
                      <span className="text-emerald-600">
                        <Plus size={12} className="inline mr-0.5" />
                        {syncStatus.summary.created_today} created today
                      </span>
                      <span className="text-blue-600">
                        <RefreshCw size={12} className="inline mr-0.5" />
                        {syncStatus.summary.updated_today} updated
                      </span>
                      {syncStatus.summary.needs_review > 0 && (
                        <span className="text-purple-600">
                          <AlertTriangle size={12} className="inline mr-0.5" />
                          {syncStatus.summary.needs_review} need review
                        </span>
                      )}
                      {syncStatus.summary.failed_today > 0 && (
                        <span className="text-red-600">
                          <X size={12} className="inline mr-0.5" />
                          {syncStatus.summary.failed_today} failed
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${showSyncDetails ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Expanded Sync Details */}
              {showSyncDetails && syncStatus.latest_sync && (
                <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-lg font-bold text-blue-700">{syncStatus.latest_sync.fetched_count || 0}</p>
                    <p className="text-xs text-slate-500">Fetched</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-lg font-bold text-emerald-700">{syncStatus.latest_sync.created_count || 0}</p>
                    <p className="text-xs text-slate-500">Created</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-lg font-bold text-amber-700">{syncStatus.latest_sync.updated_count || 0}</p>
                    <p className="text-xs text-slate-500">Updated</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-lg font-bold text-slate-600">{syncStatus.latest_sync.skipped_count || 0}</p>
                    <p className="text-xs text-slate-500">Skipped</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-lg font-bold text-red-600">{syncStatus.latest_sync.failed_count || 0}</p>
                    <p className="text-xs text-slate-500">Failed</p>
                  </div>
                  {syncStatus.latest_sync.error_summary && (
                    <div className="col-span-full text-xs text-red-600 bg-red-50 p-2 rounded-lg">
                      <strong>Errors:</strong> {syncStatus.latest_sync.error_summary}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Search & Quick Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by title, locality, bank..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9 rounded-xl"
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-36 rounded-xl" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Updated">Updated</SelectItem>
                <SelectItem value="Needs Review">Needs Review</SelectItem>
                <SelectItem value="Expired">Expired</SelectItem>
                <SelectItem value="Duplicate">Duplicate</SelectItem>
                <SelectItem value="Archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {Object.values(filters).some(v => v !== '') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {/* City */}
                <div>
                  <Label className="text-xs">City</Label>
                  <Select
                    value={filters.city}
                    onValueChange={(v) => setFilters({ ...filters, city: v === 'all' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 rounded-lg">
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cities.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Property Type */}
                <div>
                  <Label className="text-xs">Property Type</Label>
                  <Select
                    value={filters.property_type}
                    onValueChange={(v) => setFilters({ ...filters, property_type: v === 'all' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 rounded-lg">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {PROPERTY_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Bank */}
                <div>
                  <Label className="text-xs">Bank / Institution</Label>
                  <Select
                    value={filters.bank}
                    onValueChange={(v) => setFilters({ ...filters, bank: v === 'all' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 rounded-lg">
                      <SelectValue placeholder="All Banks" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Banks</SelectItem>
                      {banks.map(bank => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Priority */}
                <div>
                  <Label className="text-xs">Priority Tag</Label>
                  <Select
                    value={filters.priority_tag}
                    onValueChange={(v) => setFilters({ ...filters, priority_tag: v === 'all' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 rounded-lg">
                      <SelectValue placeholder="All Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      {PRIORITY_TAGS.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Auction Date From */}
                <div>
                  <Label className="text-xs">Auction From</Label>
                  <Input
                    type="date"
                    value={filters.auction_date_from}
                    onChange={(e) => setFilters({ ...filters, auction_date_from: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
                
                {/* Auction Date To */}
                <div>
                  <Label className="text-xs">Auction To</Label>
                  <Input
                    type="date"
                    value={filters.auction_date_to}
                    onChange={(e) => setFilters({ ...filters, auction_date_to: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
              </div>
              
              {/* Price & Discount Range */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Min Reserve Price (L)</Label>
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.reserve_price_min}
                    onChange={(e) => setFilters({ ...filters, reserve_price_min: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Reserve Price (L)</Label>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.reserve_price_max}
                    onChange={(e) => setFilters({ ...filters, reserve_price_max: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs">Min Discount %</Label>
                  <Input
                    type="number"
                    placeholder="Min %"
                    value={filters.discount_min}
                    onChange={(e) => setFilters({ ...filters, discount_min: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Discount %</Label>
                  <Input
                    type="number"
                    placeholder="Max %"
                    value={filters.discount_max}
                    onChange={(e) => setFilters({ ...filters, discount_max: e.target.value })}
                    className="mt-1 rounded-lg"
                  />
                </div>
              </div>
              
              {/* Expiring Soon Quick Buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                <span className="text-xs text-slate-500 self-center mr-2">Quick Filters:</span>
                <Button variant="outline" size="sm" onClick={() => applyQuickFilter('expiring_3d')} className="text-red-600">
                  <Timer size={12} className="mr-1" /> Expiring in 3 days
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyQuickFilter('expiring_7d')} className="text-orange-600">
                  <Timer size={12} className="mr-1" /> Expiring in 7 days
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyQuickFilter('expiring_30d')}>
                  <Timer size={12} className="mr-1" /> Expiring in 30 days
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyQuickFilter('needs_review')} className="text-purple-600">
                  <AlertTriangle size={12} className="mr-1" /> Needs Review
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyQuickFilter('high_priority')} className="text-red-600">
                  <Tag size={12} className="mr-1" /> High Priority
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-slate-400" />
          </div>
        ) : records.length === 0 ? (
          <Card className="p-8 text-center">
            <Gavel size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No auction properties found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or add a new property</p>
            <Button className="mt-4 bg-amber-600 hover:bg-amber-700" onClick={() => setShowAddDialog(true)}>
              <Plus size={14} className="mr-2" /> Add Property
            </Button>
          </Card>
        ) : (
          <>
            {/* Table View */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="auction-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">City</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Locality</th>
                      <SortableHeader field="reserve_price">Reserve</SortableHeader>
                      <SortableHeader field="estimated_market_price">Market</SortableHeader>
                      <SortableHeader field="discount_percent">Discount</SortableHeader>
                      <SortableHeader field="auction_date">Auction</SortableHeader>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Days</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Bank</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Priority</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.map((record) => (
                      <tr
                        key={record.property_id}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => openDetail(record)}
                        data-testid={`auction-row-${record.property_id}`}
                      >
                        <td className="px-3 py-3">
                          <div className="max-w-[180px]">
                            <div className="flex items-center gap-1">
                              <p className="font-medium text-slate-900 truncate text-sm">{record.title}</p>
                              {record.is_auto_ingested && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" title="Auto-ingested"></span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{record.property_type}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-700">{record.city || '—'}</td>
                        <td className="px-3 py-3 text-sm text-slate-700">{record.locality || '—'}</td>
                        <td className="px-3 py-3">
                          <span className="font-semibold text-emerald-700 text-sm">{formatLakhs(record.reserve_price)}</span>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-600">{formatLakhs(record.estimated_market_price)}</td>
                        <td className="px-3 py-3">
                          {record.discount_percent ? (
                            <span className="text-sm font-medium text-red-600">-{record.discount_percent}%</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-700">{formatDate(record.auction_date)}</td>
                        <td className="px-3 py-3">{getDaysToAuctionBadge(record.days_to_auction)}</td>
                        <td className="px-3 py-3 text-sm text-slate-600 max-w-[120px] truncate">{record.bank_or_institution || '—'}</td>
                        <td className="px-3 py-3">{getStatusBadge(record.status)}</td>
                        <td className="px-3 py-3">{getPriorityBadge(record.priority_tag)}</td>
                        <td className="px-3 py-3 text-center">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(record); }}>
                            <Eye size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Property Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={20} className="text-amber-600" />
              Add Auction Property
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {/* Basic Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <Building2 size={16} /> Basic Details
              </h4>
              <div>
                <Label className="text-xs">Title *</Label>
                <Input
                  value={addForm.title}
                  onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                  placeholder="e.g., 3BHK Flat in Powai - Bank Auction"
                  className="mt-1 rounded-xl"
                  data-testid="add-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Property Type</Label>
                  <Select
                    value={addForm.property_type}
                    onValueChange={(v) => setAddForm({ ...addForm, property_type: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Area (sq ft)</Label>
                  <Input
                    type="number"
                    value={addForm.area_sqft}
                    onChange={(e) => setAddForm({ ...addForm, area_sqft: e.target.value })}
                    placeholder="e.g., 1200"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">City</Label>
                  <Input
                    value={addForm.city}
                    onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
                    placeholder="e.g., Mumbai"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">Locality</Label>
                  <Input
                    value={addForm.locality}
                    onChange={(e) => setAddForm({ ...addForm, locality: e.target.value })}
                    placeholder="e.g., Powai"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Full Address</Label>
                <Textarea
                  value={addForm.full_address}
                  onChange={(e) => setAddForm({ ...addForm, full_address: e.target.value })}
                  placeholder="Complete address..."
                  rows={2}
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>

            {/* Auction Details */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <Gavel size={16} /> Auction Details
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Reserve Price (Lakhs)</Label>
                  <Input
                    type="number"
                    value={addForm.reserve_price}
                    onChange={(e) => setAddForm({ ...addForm, reserve_price: e.target.value })}
                    placeholder="e.g., 85"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">EMD (Lakhs)</Label>
                  <Input
                    type="number"
                    value={addForm.emd}
                    onChange={(e) => setAddForm({ ...addForm, emd: e.target.value })}
                    placeholder="e.g., 8.5"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">Auction Date</Label>
                  <Input
                    type="date"
                    value={addForm.auction_date}
                    onChange={(e) => setAddForm({ ...addForm, auction_date: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Bank / Institution</Label>
                  <Input
                    value={addForm.bank_or_institution}
                    onChange={(e) => setAddForm({ ...addForm, bank_or_institution: e.target.value })}
                    placeholder="e.g., State Bank of India"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">Occupancy Status</Label>
                  <Select
                    value={addForm.occupancy_status}
                    onValueChange={(v) => setAddForm({ ...addForm, occupancy_status: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCUPANCY_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Source Details */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <ExternalLink size={16} /> Source Details
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Source Name</Label>
                  <Input
                    value={addForm.source_name}
                    onChange={(e) => setAddForm({ ...addForm, source_name: e.target.value })}
                    placeholder="e.g., IBAPI, Manual, Newspaper"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">Source URL</Label>
                  <Input
                    value={addForm.source_url}
                    onChange={(e) => setAddForm({ ...addForm, source_url: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Internal Intelligence */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <TrendingDown size={16} /> Internal Intelligence
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Estimated Market Price (Lakhs)</Label>
                  <Input
                    type="number"
                    value={addForm.estimated_market_price}
                    onChange={(e) => setAddForm({ ...addForm, estimated_market_price: e.target.value })}
                    placeholder="e.g., 120"
                    className="mt-1 rounded-xl"
                  />
                  {addForm.reserve_price && addForm.estimated_market_price && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Discount: {(((parseFloat(addForm.estimated_market_price) - parseFloat(addForm.reserve_price)) / parseFloat(addForm.estimated_market_price)) * 100).toFixed(1)}%
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Priority Tag</Label>
                  <Select
                    value={addForm.priority_tag}
                    onValueChange={(v) => setAddForm({ ...addForm, priority_tag: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Priority</SelectItem>
                      {PRIORITY_TAGS.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Internal Notes</Label>
                <Textarea
                  value={addForm.internal_notes}
                  onChange={(e) => setAddForm({ ...addForm, internal_notes: e.target.value })}
                  placeholder="Add any internal notes or intelligence..."
                  rows={2}
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1 rounded-xl">
                Cancel
              </Button>
              <Button 
                onClick={handleAddProperty} 
                disabled={addingProperty || !addForm.title.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-xl"
                data-testid="submit-add-property"
              >
                {addingProperty ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
                Add Property
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Property Detail Drawer */}
      <Dialog open={showDetail} onOpenChange={(open) => { if (!open) { setShowDetail(false); setEditMode(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel size={20} className="text-amber-600" />
              Auction Property Details
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-slate-400" />
            </div>
          ) : selectedProperty && (
            <div className="space-y-4">
              {/* Status & Actions */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 rounded-xl">
                <div className="flex flex-wrap items-center gap-2">
                  {getStatusBadge(selectedProperty.status)}
                  {getPriorityBadge(selectedProperty.priority_tag)}
                  {selectedProperty.days_to_auction !== null && (
                    <div className="flex items-center gap-1">
                      <Timer size={14} className="text-slate-400" />
                      {getDaysToAuctionBadge(selectedProperty.days_to_auction)}
                    </div>
                  )}
                  {selectedProperty.duplicate_flag && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">Duplicate</Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {!editMode && selectedProperty.status !== 'Archived' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-btn">
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      {selectedProperty.status === 'Needs Review' && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleVerify} data-testid="verify-btn">
                          <CheckCircle size={14} className="mr-1" /> Verify
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={handleMarkDuplicate}>
                        <Copy size={14} className="mr-1" /> Duplicate
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={handleArchive} data-testid="archive-btn">
                        <Archive size={14} className="mr-1" /> Archive
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Property Info */}
              <Card className="p-4">
                {editMode ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={editForm.title || ''}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Property Type</Label>
                        <Select
                          value={editForm.property_type || 'Other'}
                          onValueChange={(v) => setEditForm({ ...editForm, property_type: v })}
                        >
                          <SelectTrigger className="mt-1 rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PROPERTY_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Area (sq ft)</Label>
                        <Input
                          type="number"
                          value={editForm.area_sqft || ''}
                          onChange={(e) => setEditForm({ ...editForm, area_sqft: parseFloat(e.target.value) || null })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input
                          value={editForm.city || ''}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Locality</Label>
                        <Input
                          value={editForm.locality || ''}
                          onChange={(e) => setEditForm({ ...editForm, locality: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Full Address</Label>
                      <Textarea
                        value={editForm.full_address || ''}
                        onChange={(e) => setEditForm({ ...editForm, full_address: e.target.value })}
                        rows={2}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Reserve Price (Lakhs)</Label>
                        <Input
                          type="number"
                          value={editForm.reserve_price || ''}
                          onChange={(e) => setEditForm({ ...editForm, reserve_price: parseFloat(e.target.value) || null })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Market Price (Lakhs)</Label>
                        <Input
                          type="number"
                          value={editForm.estimated_market_price || ''}
                          onChange={(e) => setEditForm({ ...editForm, estimated_market_price: parseFloat(e.target.value) || null })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">EMD (Lakhs)</Label>
                        <Input
                          type="number"
                          value={editForm.emd || ''}
                          onChange={(e) => setEditForm({ ...editForm, emd: parseFloat(e.target.value) || null })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Auction Date</Label>
                        <Input
                          type="date"
                          value={editForm.auction_date || ''}
                          onChange={(e) => setEditForm({ ...editForm, auction_date: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Bank / Institution</Label>
                        <Input
                          value={editForm.bank_or_institution || ''}
                          onChange={(e) => setEditForm({ ...editForm, bank_or_institution: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Occupancy Status</Label>
                        <Select
                          value={editForm.occupancy_status || 'Unknown'}
                          onValueChange={(v) => setEditForm({ ...editForm, occupancy_status: v })}
                        >
                          <SelectTrigger className="mt-1 rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OCCUPANCY_STATUSES.map(status => (
                              <SelectItem key={status} value={status}>{status}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Priority Tag</Label>
                        <Select
                          value={editForm.priority_tag || 'none'}
                          onValueChange={(v) => setEditForm({ ...editForm, priority_tag: v === 'none' ? null : v })}
                        >
                          <SelectTrigger className="mt-1 rounded-xl">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Priority</SelectItem>
                            {PRIORITY_TAGS.map(tag => (
                              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Source URL</Label>
                      <Input
                        value={editForm.source_url || ''}
                        onChange={(e) => setEditForm({ ...editForm, source_url: e.target.value })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Internal Notes</Label>
                      <Textarea
                        value={editForm.internal_notes || ''}
                        onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
                        rows={3}
                        className="mt-1 rounded-xl"
                        placeholder="Add internal notes..."
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" onClick={() => setEditMode(false)} className="flex-1 rounded-xl">
                        Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-xl">
                        {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-lg text-slate-900 mb-3">{selectedProperty.title}</h3>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-start gap-2">
                        <Building2 size={16} className="text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Property Type</p>
                          <p className="font-medium text-slate-900">{selectedProperty.property_type}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin size={16} className="text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Location</p>
                          <p className="font-medium text-slate-900">
                            {selectedProperty.locality || '—'}, {selectedProperty.city || '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <IndianRupee size={16} className="text-emerald-500 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Reserve Price</p>
                          <p className="font-semibold text-emerald-700">{formatLakhs(selectedProperty.reserve_price)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <IndianRupee size={16} className="text-blue-500 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Market Price</p>
                          <p className="font-medium text-blue-700">{formatLakhs(selectedProperty.estimated_market_price)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <TrendingDown size={16} className="text-red-500 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Discount</p>
                          <p className="font-medium text-red-600">
                            {selectedProperty.discount_percent ? `${selectedProperty.discount_percent}% below market` : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <IndianRupee size={16} className="text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">EMD</p>
                          <p className="font-medium text-slate-700">{formatLakhs(selectedProperty.emd)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Calendar size={16} className="text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Auction Date</p>
                          <p className="font-medium text-slate-900">{formatDate(selectedProperty.auction_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <FileText size={16} className="text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">Bank/Institution</p>
                          <p className="font-medium text-slate-900">{selectedProperty.bank_or_institution || '—'}</p>
                        </div>
                      </div>
                      {selectedProperty.area_sqft && (
                        <div className="flex items-start gap-2">
                          <Ruler size={16} className="text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-slate-500">Area</p>
                            <p className="font-medium text-slate-900">{selectedProperty.area_sqft} sq ft</p>
                          </div>
                        </div>
                      )}
                      {selectedProperty.occupancy_status && (
                        <div className="flex items-start gap-2">
                          <Home size={16} className="text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-slate-500">Occupancy</p>
                            <p className="font-medium text-slate-900">{selectedProperty.occupancy_status}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {selectedProperty.full_address && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Full Address</p>
                        <p className="text-sm text-slate-700">{selectedProperty.full_address}</p>
                      </div>
                    )}
                    
                    {selectedProperty.internal_notes && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-xs text-amber-600 font-medium mb-1 flex items-center gap-1">
                          <StickyNote size={12} /> Internal Notes
                        </p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedProperty.internal_notes}</p>
                      </div>
                    )}
                    
                    {/* Source Info */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <p className="text-slate-500">Source: <span className="font-medium text-slate-700">{selectedProperty.source_name}</span></p>
                          <p className="text-xs text-slate-400">Last synced: {formatDate(selectedProperty.last_synced_at)}</p>
                        </div>
                        {selectedProperty.source_url && (
                          <a
                            href={selectedProperty.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <ExternalLink size={14} /> View Source
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Card>

              {/* Audit Logs */}
              {!editMode && auditLogs.length > 0 && (
                <Card className="p-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    Activity Log
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {auditLogs.map((log, idx) => (
                      <div key={log.log_id || idx} className="flex items-start gap-2 text-sm p-2 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-slate-700 capitalize">{log.action.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500">
                            by {log.performed_by_name} • {formatDate(log.performed_at)}
                          </p>
                          {log.changes && Object.keys(log.changes).length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                              Changed: {Object.keys(log.changes).filter(k => k !== 'updated_at' && k !== 'last_synced_at').join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuctionSupply;
